// Каталог колонок ведомости — то единственное, чем ведомость отличается от табеля и от
// официального контура в общей сетке.
//
// ## Заголовок НЕ пишется здесь ни разу
//
// Он приходит с сервера полем `title` — это бланк заказчика, и именно он уходит в печать.
// У заказчика заголовки официальной пары стоят обратно смыслу слов: в объектовой ведомости
// «Официально получать» держит УЖЕ ВЫПЛАЧЕННОЕ, а «Официально выдано» — ОСТАТОК; в
// административной пара стоит наоборот. Проверено формулой в его ячейке: 85 000 − 25 935 −
// 25 934 = 33 131, где 25 935 стоит под «получать».
//
// Поэтому переименования нет, а рядом с заголовком стоит ПОДСКАЗКА ПО СМЫСЛУ, и берётся она из
// КОДА колонки (`official_paid` → «уже выплачено»), а не из его слов. Код назван по смыслу
// сервером, слова — заказчиком; связывать подсказку со словами значило бы получить ту же
// перевёрнутую пару, только на два экрана дальше. Это самое опасное место во всём кабинете:
// перепутанная пара стоит денег на 281 строке сразу.
//
// ## Ноль и «не знаем» — разные вещи, и здесь их три
//
// 1. Расчёта по человеку ещё не было (`calculated_at` пусто) — сумма НЕИЗВЕСТНА, показывается
//    прочерк. Ноль здесь означал бы «к выплате ничего», а это неправда.
// 2. Ноль по каскаду (`flag_zeroed_by_cascade`) — серая заливка и пояснение «не удержано: не
//    хватило начисленного». Строка идёт в полосу проверки.
// 3. Ноль оттого, что начисления равны официальной части, — ОБЫЧНАЯ строка без пометки
//    (сценарий 84). Общий признак «итог нулевой» превратил бы полосу проверки в шум, и её
//    перестали бы читать.
//
// ## Ставка не вводится числом ни в одной ячейке
//
// Колонка тарифа считается движком и по нажатию раскрывает панель строки, где правка законна:
// сумма за период и норма либо целевая сумма. Обратный ход превращает взыскание 5 000 в 5 044,
// поэтому поля ввода ставки в ведомости нет и не будет.

import type {
  PayrollColumnOut,
  PayrollEmployeeOut,
  PayrollRowOut,
  PayrollTotalsOut,
} from '@/shared/api/endpoints/payroll';
import {
  COLUMN_ACCRUED,
  COLUMN_ADVANCE,
  COLUMN_HOURS,
  COLUMN_OFFICIAL_PAID,
  COLUMN_OFFICIAL_REST,
  COLUMN_RATE,
  COLUMN_TOTAL,
} from '@/shared/api/endpoints/payroll';
import type { CellState, GridColumn } from '@/shared/grid';
import { formatHours } from '@/shared/hours/format';
import { formatKopecks, formatRateWholeRubles } from '@/shared/money/format';
import { moneyErrorText, parseRublesToKopecks } from '@/shared/money/parse';
import { countLabel } from '@/shared/text/plural';
import { formatDateOnly } from '@/shared/time/format';

import { PANEL_SHEET_PERSON, PANEL_SHEET_ROW } from '../route';

import { sumShown, type SheetRow } from './rows';
import { ТЕКСТ_ПРИЧИНЫ, неизвестноИз, причинаПустоты } from './причины';
import { авансВКолонке, перенесённыйАванс } from './аванс';
import { подсказкаМесяца } from './месяц';

/** Колонка фамилии: она вне шаблона заказчика, без неё ведомость перестаёт быть ведомостью. */
export const COLUMN_EMPLOYEE = 'employee';
/** Колонка поста: у человека бывает две тарифные строки, и без поста их не различить. */
export const COLUMN_POST = 'post';

/**
 * Подсказка по смыслу — по КОДУ колонки, а не по её словам.
 *
 * Только там, где слово заказчика само по себе смысла не передаёт либо передаёт обратный.
 * Дописывать сюда подпись к каждой колонке не нужно: подпись, повторяющая заголовок, съедает
 * вторую строку шапки, а строка шапки — это строка сетки, которой человек не увидит.
 */
const ПОДСКАЗКА_ПО_КОДУ: Readonly<Record<string, string>> = {
  [COLUMN_HOURS]: 'факт по табелю',
  [COLUMN_RATE]: '₽ в час',
  // Не «за период × часы ÷ норма»: у прямой ставки формула другая — часы × ставка, — и
  // подпись, верная только для одного режима, на объектах Коротчаево врала бы в каждой строке.
  [COLUMN_ACCRUED]: 'по часам и тарифу',
  [COLUMN_ADVANCE]: 'удержано авансов',
  // ⚠️ Пара, перевёрнутая в бланке заказчика. Подпись держит СМЫСЛ, заголовок — его слова.
  [COLUMN_OFFICIAL_PAID]: 'уже выплачено',
  [COLUMN_OFFICIAL_REST]: 'остаток к выплате',
  [COLUMN_TOTAL]: 'на карту',
};

/**
 * Что колонка значит на самом деле: показывается при наведении на заголовок и с клавиатуры.
 *
 * Отличается от подписи под заголовком тем же, чем абзац от ярлыка. Подпись отвечает на «что
 * это» в двух словах и стоит всегда; пояснение отвечает на «откуда это число» и «почему его
 * здесь не поправить» — и стоит там, где спрашивают.
 *
 * До 04.09.2026 объяснение в продукте БЫЛО и не показывалось нигде: оно доходило до человека
 * только тогда, когда он пытался ввести число в нередактируемую ячейку. То есть узнавал о нём
 * тот, кто уже ошибся, и не узнавал тот, кто просто хотел понять ведомость.
 */
const ПОЯСНЕНИЕ_ПО_КОДУ: Readonly<Record<string, string>> = {
  [COLUMN_HOURS]: 'Факт часов по табелю за период. Правится в табеле, а не здесь.',
  [COLUMN_RATE]:
    'Ставка за час. Хранится дробной, а показывается целой: считать надо от суммы за период, ' +
    'иначе человек недополучит на округлении.',
  [COLUMN_ACCRUED]:
    'Начислено по этой тарифной строке: сумма за период умножить на факт часов и разделить ' +
    'на норму.',
  [COLUMN_ADVANCE]:
    'Аванс этого месяца — принятые заявки подотчёта с этим получателем — удерживается первым, ' +
    'до прочих удержаний. Сюда же ложится неудержанный аванс прошлых месяцев: он удерживается ' +
    'вместе с долгами.',
  // ⚠️ Пара, перевёрнутая в бланке заказчика. Ради неё пояснение и заводилось.
  [COLUMN_OFFICIAL_PAID]:
    'Уже выплачено официально в связанном периоде. Заголовок бланка про «получать» — слова ' +
    'заказчика; значение здесь про выплаченное. Вместе с остатком вычитается из начисленного ' +
    'до каскада удержаний.',
  [COLUMN_OFFICIAL_REST]:
    'Остаток официальной части к выплате. Вместе с уже выплаченным вычитается из начисленного ' +
    'до каскада удержаний: 85 000 минус 25 935 минус 25 934 равно 33 131.',
  [COLUMN_TOTAL]: 'Всего к выплате после каскада удержаний — то, что уходит человеку на карту.',
};

/** Ширина колонки по коду; всё остальное — по виду колонки. Оформление, а не договор. */
const ШИРИНА_ПО_КОДУ: Readonly<Record<string, number>> = {
  [COLUMN_HOURS]: 84,
  [COLUMN_RATE]: 92,
  [COLUMN_ACCRUED]: 132,
  [COLUMN_ADVANCE]: 124,
  [COLUMN_OFFICIAL_PAID]: 152,
  [COLUMN_OFFICIAL_REST]: 152,
  [COLUMN_TOTAL]: 144,
  payout_details: 148,
  salary_on_sites: 152,
  bonus_other: 130,
};

const ШИРИНА_ПО_ВИДУ: Readonly<Record<string, number>> = {
  accrual: 120,
  deduction: 116,
  official_split: 148,
  service: 120,
  total: 144,
};

/** Коды колонок, чьё значение сервер отдаёт отдельным полем ЧЕЛОВЕКА. */
const ПОЛЯ_ЧЕЛОВЕКА = new Set<string>([
  COLUMN_ADVANCE,
  COLUMN_OFFICIAL_PAID,
  COLUMN_OFFICIAL_REST,
  COLUMN_TOTAL,
]);

/** На чём висит значение колонки: на тарифной строке или на человеке целиком. */
type Уровень = 'row' | 'employee';

/**
 * Где лежит значение этой колонки у ЭТОГО человека — по факту, а не по виду колонки.
 *
 * Вид колонки для этого не годится. Контракт разрешает серверу класть на человека и
 * «внетарифные начисления» тоже: премия за кандидата к тарифной строке не привязана. Решив по
 * виду «начисление значит на строке», мы показали бы такую премию в КАЖДОЙ строке человека с
 * двумя постами и удвоили бы её в подвале. Зеркально: удержание, положенное сервером на строку,
 * пропало бы из всех строк, кроме первой, и обнулилось в итоге столбца.
 *
 * Ответ один на все строки человека: иначе первая его строка и вторая разошлись бы в том,
 * откуда брать число.
 */
function уровень(колонка: PayrollColumnOut, человек: PayrollEmployeeOut): Уровень {
  if (ПОЛЯ_ЧЕЛОВЕКА.has(колонка.code)) {
    return 'employee';
  }
  if (колонка.code === COLUMN_ACCRUED) {
    return 'row';
  }
  if (человек.amounts?.[колонка.code] !== undefined) {
    return 'employee';
  }
  if ((человек.rows ?? []).some((строка) => строка.amounts?.[колонка.code] !== undefined)) {
    return 'row';
  }
  // Значения нет вообще ни там, ни там. Тогда решает вид колонки: удержания и итог висят на
  // человеке по устройству каскада, начисления считаются по тарифным строкам.
  return колонка.kind === 'deduction' ||
    колонка.kind === 'total' ||
    колонка.kind === 'official_split'
    ? 'employee'
    : 'row';
}

export interface SheetColumnsOptions {
  /** Колонки шаблона, как их прислал сервер: уже отобранные и в нужном порядке. */
  readonly columns: readonly PayrollColumnOut[];
  /** Итоги ведомости с сервера: подвал берёт их, а не складывает сам. */
  readonly totals: PayrollTotalsOut;
  /** Расчёт по ведомости проводился хотя бы у кого-то. */
  readonly calculated: boolean;
  /**
   * Можно ли править ячейки прямо сейчас.
   *
   * Пока сервер не отдаёт операции правки ячейки, значение ложно у всех, и колонки с
   * поведением «вводится» становятся заблокированными С ПРИЧИНОЙ, а не молча нажимаемыми.
   */
  readonly canEdit: boolean;
  /** Почему править нельзя. Обязательно, когда `canEdit` ложно: молчание читается как поломка. */
  readonly blockedReason: string;
  /**
   * Месяц ведомости словами с сервера, «сентябрь 2026»: для подсказки «За сентябрь 2026 форма
   * удерживается в ведомости «Бур-Сервис»». Пусто — подсказка скажет «за этот месяц».
   */
  readonly periodTitle?: string | null;
  /** Раскрыть тарифную строку: часы, ставка, из чего сложилось начисление. */
  readonly onOpenRow: (row: SheetRow) => void;
  /** Раскрыть человека: каскад по шагам, официальная часть, удержания. */
  readonly onOpenPerson: (row: SheetRow) => void;
}

export function buildColumns({
  columns,
  totals,
  calculated,
  canEdit,
  blockedReason,
  periodTitle,
  onOpenRow,
  onOpenPerson,
}: SheetColumnsOptions): GridColumn<SheetRow>[] {
  const фамилия: GridColumn<SheetRow> = {
    id: COLUMN_EMPLOYEE,
    title: 'Фамилия и имя',
    width: 216,
    sticky: true,
    always: true,
    align: 'left',
    cell: (строка): CellState => {
      const продолжение = !строка.isFirstOfEmployee;
      return {
        text: строка.employee.employee_name,
        behaviour: 'linked',
        reason:
          (продолжение
            ? `Вторая тарифная строка того же человека (${строка.indexInEmployee} из ${строка.rowsOfEmployee}). Удержания и итог у него одни на все строки и стоят в первой. `
            : '') +
          'Каскад удержаний, официальная часть и итог разобраны в панели человека. Сама фамилия правится в карточке.',
        render: (
          <button
            type='button'
            onClick={(событие) => {
              событие.stopPropagation();
              onOpenPerson(строка);
            }}
            className='flex w-full items-center gap-1 text-left'
          >
            <span
              className={
                продолжение
                  ? 'min-w-0 truncate text-text-sub-600'
                  : 'min-w-0 truncate text-text-strong-950 underline decoration-stroke-soft-200 underline-offset-2'
              }
            >
              {строка.employee.employee_name}
            </span>
            {строка.row?.is_excluded === true && (
              // Строка выведена из расчёта. Пометка стоит В ОДНУ строку с фамилией: вторая
              // строка распирает строку сетки, а виртуализация раскладывает их по 28 точек —
              // на ведомости в 281 строку расхождение накапливается, и внизу каретка встаёт
              // не туда, куда смотрит человек.
              <span className='shrink-0 text-grid-sm text-warning-base'>вне расчёта</span>
            )}
            {продолжение && (
              <span className='shrink-0 text-grid-sm text-text-sub-600'>
                строка {строка.indexInEmployee}
              </span>
            )}
          </button>
        ),
      };
    },
    footer: (): CellState => ({ text: 'Итого по ведомости', behaviour: 'computed' }),
  };

  const пост: GridColumn<SheetRow> = {
    id: COLUMN_POST,
    title: 'Пост',
    subtitle: 'тарифная строка',
    width: 148,
    always: true,
    align: 'left',
    cell: (строка): CellState => {
      if (строка.row === null) {
        return {
          text: '',
          behaviour: 'linked',
          reason:
            'У человека в этой ведомости нет ни одной тарифной строки: на пост он не назначен. ' +
            'Удержания и официальная часть при этом могут быть, поэтому из ведомости он не убран.',
        };
      }
      if (строка.row.is_excluded) {
        return {
          text: строка.row.post_name ?? строка.row.row_type_title,
          behaviour: 'blocked',
          reason: `Строка выведена из расчёта: ${строка.row.exclusion_basis ?? 'основание не записано'}.`,
          className: 'text-warning-base',
        };
      }
      return {
        text: строка.row.post_name ?? строка.row.row_type_title,
        behaviour: 'linked',
        reason: `${строка.row.row_type_title}. Пост меняется переводом в карточке человека; часы, ставка и начисление разобраны в панели строки.`,
        // Пост И ЕСТЬ тарифная строка: раскрывать её отсюда — самый короткий путь к часам и
        // тарифу. Заголовок часов у заказчика в разных раскладках разный («Кол-во часов»,
        // «Вышло»), а пост стоит в каждой и называется одинаково.
        source: { panel: PANEL_SHEET_ROW, id: строка.row.id, focus: COLUMN_POST },
      };
    },
    footer: (строки): CellState => ({
      text: `${countLabel(totals.employees_count, ['человек', 'человека', 'человек'])}, ${countLabel(
        строки.length,
        ['строка', 'строки', 'строк'],
      )}`,
      behaviour: 'computed',
    }),
  };

  const шаблонные = columns.map((колонка) =>
    колонкаШаблона(колонка, {
      totals,
      calculated,
      canEdit,
      blockedReason,
      periodTitle: periodTitle ?? null,
      onOpenRow,
      onOpenPerson,
    }),
  );

  return [фамилия, пост, ...шаблонные];
}

interface КонтекстКолонки {
  readonly totals: PayrollTotalsOut;
  readonly calculated: boolean;
  readonly canEdit: boolean;
  readonly blockedReason: string;
  readonly periodTitle: string | null;
  readonly onOpenRow: (row: SheetRow) => void;
  readonly onOpenPerson: (row: SheetRow) => void;
}

/**
 * Пояснение к заголовку колонки. Пусто — пояснять нечего, и подсказки у заголовка не будет.
 *
 * ## Слово сервера главнее нашего
 *
 * У колонки есть пояснение заказчика (`source_note`), записанное про ЕГО бланк: «заголовок
 * стоит обратно смыслу слова, „получать“ держит уже выплаченное, проверено формулой в его
 * ячейке». Наше — про устройство расчёта вообще.
 *
 * Показывается ОДНО из двух, а не оба подряд. Первая версия склеивала их, и на «Официально
 * получать» человек получал девять строк, где одна и та же арифметика заказчика была
 * рассказана дважды разными словами. Проверено кадром.
 */
function пояснениеЗаголовка(колонка: PayrollColumnOut): string | undefined {
  const своё =
    ПОЯСНЕНИЕ_ПО_КОДУ[колонка.code] ??
    (колонка.kind === 'deduction'
      ? 'Удержание по человеку, а не по тарифной строке: применяется ровно один раз, ' +
        'сколько бы постов у него ни было.'
      : undefined);
  const главное =
    колонка.source_note !== null && колонка.source_note !== '' ? колонка.source_note : своё;
  if (главное === undefined) {
    return undefined;
  }
  const почемуНельзя =
    колонка.behaviour === 'locked' && колонка.lock_reason !== null ? колонка.lock_reason : null;
  return почемуНельзя === null ? главное : `${точкаВКонце(главное)} ${почемуНельзя}.`;
}

/** Точка в конце фразы, если её там нет: две фразы подряд без точки читаются как одна. */
function точкаВКонце(фраза: string): string {
  return /[.!?]$/.test(фраза.trimEnd()) ? фраза.trimEnd() : `${фраза.trimEnd()}.`;
}

function колонкаШаблона(
  колонка: PayrollColumnOut,
  контекст: КонтекстКолонки,
): GridColumn<SheetRow> {
  const ширина = ШИРИНА_ПО_КОДУ[колонка.code] ?? ШИРИНА_ПО_ВИДУ[колонка.kind] ?? 124;
  // Подпись по смыслу — по коду; если её нет, вид колонки словами с сервера. Заголовок при
  // этом остаётся заголовком заказчика и в вебе не переписывается ни при каких условиях.
  const подпись = ПОДСКАЗКА_ПО_КОДУ[колонка.code] ?? колонка.kind_title.toLowerCase();

  return {
    id: колонка.code,
    // Заголовок ЗАКАЗЧИКА, как его прислал сервер. Здесь он не собирается и не правится.
    title: колонка.title,
    subtitle: подпись,
    hint: пояснениеЗаголовка(колонка),
    width: ширина,
    // Служебные колонки, кроме часов, — это текст (реквизиты выплаты), и он прижимается влево:
    // текст, выровненный по правому краю, читается как число, которого там нет.
    align: текстовая(колонка) ? 'left' : 'right',
    // Цвета у заголовка нет намеренно. До 25.08.2026 официальная пара стояла предупреждающим
    // оранжевым — две колонки из восемнадцати, без единого слова почему, — и читалось это как
    // «с этими колонками что-то не так». Различает их подпись под заголовком: «уже выплачено»
    // и «остаток к выплате». Заголовки заказчика в объектовой ведомости стоят обратно смыслу
    // слов, и разбирается это чтением подписи, а не цветом, который ни на что не указывает.
    cell: (строка) => ячейка(колонка, строка, контекст),
    footer: (строки) => подвал(колонка, строки, контекст),
  };
}

/**
 * Колонка «Реквизиты выплаты»: последние четыре цифры и дата, когда выплатили.
 *
 * Сценарий 76 требует в строке ровно это. До 26.08.2026 колонка стояла пустой у всех до
 * единого: сервер не отдавал ни реквизитов, ни факта выплаты, и «Реквизиты выплаты» выглядели
 * как забытая колонка. Пустое, нулевое и неизвестное здесь тоже три разных факта: реквизитов
 * нет вовсе, реквизиты есть и выплаты не было, выплата прошла.
 */
function ячейкаРеквизитов(строка: SheetRow): CellState {
  const человек = строка.employee;
  const хвост = человек.payout_last4 === null ? null : `•••• ${человек.payout_last4}`;
  const выплачен = человек.paid_at === null ? null : formatDateOnly(человек.paid_at);

  if (хвост === null && выплачен === null) {
    return {
      text: '',
      behaviour: 'linked',
      reason:
        'Заявления о реквизитах у человека нет: платить некуда, и в печатной форме эта клетка ' +
        'останется пустой. Заявление заводится в его карточке, вкладка «Выплаты».',
      source: { panel: PANEL_SHEET_PERSON, id: человек.id, focus: 'payout_details' },
    };
  }
  return {
    text: [хвост, выплачен].filter((часть) => часть !== null).join(' · '),
    behaviour: 'linked',
    reason:
      выплачен === null
        ? 'Реквизиты из действующего заявления. Выплата по этой строке пока не отмечена — отметить её можно в панели человека.'
        : `Выплачено ${выплачен}${человек.paid_amount === null ? '' : ` на ${formatKopecks(человек.paid_amount)}`}. Номер целиком не показывается ни здесь, ни в печати: только последние четыре цифры.`,
    source: { panel: PANEL_SHEET_PERSON, id: человек.id, focus: 'payout_details' },
  };
}

/** Часы и ставка живут на тарифной строке и деньгами не являются. */
function ячейкаСтроки(колонка: PayrollColumnOut, строка: SheetRow): CellState | null {
  if (колонка.code === COLUMN_HOURS) {
    return {
      text: formatHours(строка.row?.hours ?? null),
      behaviour: 'linked',
      reason:
        строка.row === null
          ? 'Тарифной строки нет, значит нет и часов.'
          : 'Часы приходят из табеля объекта за этот месяц. Править их надо там: ведомость их только читает.',
      source:
        строка.row === null
          ? undefined
          : { panel: PANEL_SHEET_ROW, id: строка.row.id, focus: колонка.code },
    };
  }
  if (колонка.code === COLUMN_RATE) {
    return {
      text: formatRateWholeRubles(строка.row?.rate ?? null),
      behaviour: 'computed',
      // Правило 5: ставка в ячейке не вводится. 85 000 при норме 558 показывается как 152, и
      // ввести 152 руками невозможно ни в одной ячейке.
      reason: почемуТакаяСтавка(строка.row),
      source:
        строка.row === null
          ? undefined
          : { panel: PANEL_SHEET_ROW, id: строка.row.id, focus: колонка.code },
    };
  }
  return null;
}

/**
 * Подсказка ячейки ставки — своя у каждого способа задать тариф.
 *
 * До 23.09.2026 она у всех строк говорила «ставка считается из суммы за период и нормы часов,
 * а не вводится числом», хотя у 124 назначений из 501 в копии боевой базы ставка задана прямо —
 * рублями за час, числом (объекты Коротчаево, 171 ₽). Там нет ни суммы за период, ни нормы, и
 * объяснение отправляло человека искать то, чего не существует. «Показана целым рублём, а
 * считается с шестью знаками» — тоже наши слова, а не его: ему важно не число знаков, а то,
 * что начисление от округлённой ставки не считается.
 */
function почемуТакаяСтавка(строка: PayrollRowOut | null): string {
  if (строка === null) {
    return 'Тарифной строки нет, значит нет и ставки.';
  }
  if (строка.row_type === 'salary') {
    return 'У строки-оклада ставки нет: начисление задано суммой.';
  }
  if (строка.rate_mode === 'direct') {
    return (
      'Ставка задана прямо, рублями за час: начисление — ставка × часы. Изменить её можно в ' +
      'панели строки.'
    );
  }
  if (строка.rate_mode === 'by_target') {
    return (
      'Ставка выведена из целевой суммы и часов, а не вводится числом. Здесь она округлена до ' +
      'рубля, а начисление равно целевой сумме. Изменить её можно целевой суммой — в панели ' +
      'строки.'
    );
  }
  return (
    'Ставка считается из суммы за период и нормы часов, а не вводится числом. Здесь она ' +
    'округлена до рубля, а начисление считается точно: от округлённой ставки вышло бы, ' +
    'например, 84 816 вместо 85 000. Изменить её можно парой «сумма за период и норма» либо ' +
    'целевой суммой — в панели строки.'
  );
}

/** Колонка держит текст, а не число: реквизиты выплаты и прочие служебные. */
function текстовая(колонка: PayrollColumnOut): boolean {
  return (
    колонка.kind === 'service' && колонка.code !== COLUMN_HOURS && колонка.code !== COLUMN_RATE
  );
}

function ячейка(колонка: PayrollColumnOut, строка: SheetRow, контекст: КонтекстКолонки): CellState {
  const прямая = ячейкаСтроки(колонка, строка);
  if (прямая !== null) {
    return прямая;
  }

  if (колонка.code === 'payout_details') {
    // Реквизиты висят на человеке: показываем в первой его строке, как и удержания.
    return строка.isFirstOfEmployee
      ? ячейкаРеквизитов(строка)
      : {
          text: '',
          behaviour: 'linked',
          reason: 'Реквизиты у человека одни на все его тарифные строки: они в первой.',
          source: { panel: PANEL_SHEET_PERSON, id: строка.employee.id, focus: колонка.code },
        };
  }

  const наЧеловеке = уровень(колонка, строка.employee) === 'employee';
  // Значение человека показывается ровно один раз — в первой его строке. Повторив удержание
  // во второй, мы удвоили бы столбец: у Ванина Н. форма удержана один раз на две строки.
  if (наЧеловеке && !строка.isFirstOfEmployee) {
    return {
      text: '',
      behaviour: 'linked',
      reason:
        'Удержания, официальная часть и итог висят на человеке, а не на тарифной строке: они ' +
        `стоят в первой его строке. Сложить их дважды нельзя — человеку удержат вдвое.`,
      source: { panel: PANEL_SHEET_PERSON, id: строка.employee.id, focus: колонка.code },
    };
  }

  const значение = суммаЯчейки(колонка, строка);
  const рассчитан = строка.employee.calculated_at !== null;

  // Колонка, вводимая с клавиатуры, разбирается ДО разбора «значения нет»: пустая премия — это
  // «не проставлено», а не «сервер про неё молчит», и ячейка обязана остаться вводимой.
  if (колонка.behaviour === 'input' && контекст.canEdit) {
    if (колонка.requires_basis) {
      // Основание в ячейку не помещается, а без него сервер откажет — уже после того, как
      // человек решил, что ввод принят. Поэтому такая колонка ведёт в панель, где поле есть.
      return {
        text: значение === null || значение === 0 ? '' : formatKopecks(значение),
        behaviour: 'linked',
        reason: причина(
          колонка,
          контекст,
          'Эта колонка требует основания: без него сумма не сохранится, а вписать его в ячейку негде. Введите её в панели человека — там есть поле основания.',
        ),
        source: источник(колонка, строка, наЧеловеке),
      };
    }
    return {
      text: значение === null || значение === 0 ? '' : formatKopecks(значение),
      behaviour: 'input',
      raw: рублямиДляПравки(значение ?? 0),
      reason: причина(колонка, контекст, поясненияПоКоду(колонка, строка)),
    };
  }

  // Суммы по колонке нет. Дальше три РАЗНЫХ случая, и слипаться в одну пустую ячейку они не
  // имеют права: на живом прогоне 25.08.2026 половина денежных колонок стояла пустой, а
  // соседняя официальная — с «0,00», и на одном и том же экране один и тот же факт «ничего»
  // выглядел двумя способами. Пустая ячейка в денежной таблице читается как «не заполнили».
  if (значение === null) {
    // Служебная колонка держит ТЕКСТ (реквизиты выплаты): «0,00» в ней означало бы сумму,
    // которой там не бывает вовсе.
    if (текстовая(колонка)) {
      return {
        text: '',
        behaviour: поведение(колонка, контекст),
        reason: причина(колонка, контекст, поясненияПоКоду(колонка, строка)),
        source: источник(колонка, строка, наЧеловеке),
      };
    }
    // Тарифной строки нет вовсе: начисления по ней не «ноль», её просто нет.
    if (строка.row === null && !наЧеловеке) {
      return {
        text: '',
        behaviour: поведение(колонка, контекст),
        reason: причина(
          колонка,
          контекст,
          'У человека нет тарифной строки в этой ведомости, значит нет и начисления по ней. Из ведомости его не убрали: удержания и официальная часть у него могут быть.',
        ),
        source: источник(колонка, строка, наЧеловеке),
      };
    }
    // Расчёта не было: величина НЕИЗВЕСТНА. Ноль тут означал бы «ничего не положено».
    if (!рассчитан) {
      return {
        text: '—',
        behaviour: поведение(колонка, контекст),
        reason: причина(колонка, контекст, почемуНеПосчитан(строка, контекст.calculated)),
        source: источник(колонка, строка, наЧеловеке),
      };
    }
    // Расчёт прошёл, а суммы нет — значит по этой колонке НОЛЬ, и это ответ, а не молчание.
    // Приглушённо, чтобы восемнадцать колонок нулей не спорили за внимание с настоящими
    // числами: ноль остаётся числом, только тише.
    //
    // Если это удержание стоит в другой ведомости месяца, ноль объясняется ею: «не удержано
    // ничего» про форму, которую удержали в «Бур-Сервисе», — неправда (`./месяц`).
    const вМесяце = подсказкаМесяца(колонка, строка.employee, 0, контекст.periodTitle);
    return {
      text: formatKopecks(0),
      behaviour: поведение(колонка, контекст),
      className: 'text-text-sub-600',
      reason: причина(
        колонка,
        контекст,
        вМесяце ??
          (колонка.kind === 'deduction'
            ? 'Расчёт прошёл, и по этой колонке не удержано ничего. Это ноль, а не «не заполнено».'
            : 'Расчёт прошёл, и по этой колонке не начислено ничего. Это ноль, а не «не заполнено».'),
      ),
      source: источник(колонка, строка, наЧеловеке),
    };
  }

  // Расчёта не было, и число нулевое: это НЕИЗВЕСТНО, а не ноль. Ноль здесь читается как
  // «к выплате ничего», и по такой ячейке решают, доплачивать ли человеку.
  if (!рассчитан && значение === 0) {
    return {
      text: '—',
      behaviour: поведение(колонка, контекст),
      reason: причина(колонка, контекст, почемуНеПосчитан(строка, контекст.calculated)),
      source: источник(колонка, строка, наЧеловеке),
    };
  }

  // Расчёт прошёл, а начисление и итог — ноль ПО ПРИЧИНЕ, которую знает сервер: нет тарифа или
  // нет часов. Это тоже не ноль, а «не знаем»: человек мог отработать месяц. До 23.09.2026 здесь
  // стоял «0», а под итогом — «ноль штатный: начисления равны официальной части», то есть
  // объяснение совсем другого случая.
  const пустотаСтроки = причинаПустоты(
    строка.employee,
    колонка.code === COLUMN_ACCRUED ? строка.row : null,
  );
  if (
    значение === 0 &&
    (колонка.code === COLUMN_ACCRUED || колонка.code === COLUMN_TOTAL) &&
    неизвестноИз(пустотаСтроки)
  ) {
    return {
      text: '—',
      behaviour: поведение(колонка, контекст),
      reason: причина(колонка, контекст, ТЕКСТ_ПРИЧИНЫ[пустотаСтроки!]),
      source: источник(колонка, строка, наЧеловеке),
    };
  }

  const обнулён = строка.employee.flag_zeroed_by_cascade;

  // Ноль по каскаду — серая заливка с пояснением. Второй ноль, от равенства начислений
  // официальной части, остаётся ОБЫЧНОЙ строкой без пометки (сценарий 84).
  if (значение === 0 && обнулён && (колонка.kind === 'deduction' || колонка.kind === 'total')) {
    // Причин у такого ноля бывает две сразу: здесь не хватило начисленного, а удержание месяца
    // стоит в другой ведомости. Говорим обе — первая не отменяет вторую.
    const вМесяце = подсказкаМесяца(колонка, строка.employee, 0, контекст.periodTitle);
    return {
      text: formatKopecks(0),
      behaviour: поведение(колонка, контекст),
      className: 'bg-bg-weak-50',
      reason: причина(
        колонка,
        контекст,
        'Не удержано: не хватило начисленного. Авансы удержаны полностью, остаток ушёл в долг, ' +
          'прочие удержания не проставлены. Строка вынесена в полосу проверки.' +
          (вМесяце === null ? '' : ` ${вМесяце}`),
      ),
      source: источник(колонка, строка, наЧеловеке),
    };
  }

  if (колонка.code === COLUMN_TOTAL && значение === 0 && !обнулён) {
    // Штатный ноль: начисления равны официальной части, на карту не идёт ничего. Пометки нет
    // намеренно — иначе полоса проверки заполнится строками, в которых всё правильно.
    //
    // Только когда начислено БЫЛО. Ноль начисленного — другой случай, и «начисления равны
    // официальной части» про него неправда: равны они потому, что обе стороны пусты.
    return {
      text: formatKopecks(0),
      behaviour: поведение(колонка, контекст),
      reason: причина(
        колонка,
        контекст,
        строка.employee.accruals_total > 0
          ? 'Ноль штатный: начисления равны официальной части, на карту не идёт ничего. Это не ' +
              'замечание, и в полосу проверки строка не попадает.'
          : 'Начислено ноль, поэтому и на карту ноль: в этом месяце у человека нет ни часов, ни ' +
              'окладной суммы.',
      ),
      source: источник(колонка, строка, наЧеловеке),
    };
  }

  // Удержание месяца разложено по ведомостям: ноль здесь объясняется соседней, а сумма
  // дополняется тем, что стоит в соседней (`./месяц`).
  const вМесяце = подсказкаМесяца(колонка, строка.employee, значение, контекст.periodTitle);
  const своё = поясненияПоКоду(колонка, строка);
  return {
    text: formatKopecks(значение),
    behaviour: поведение(колонка, контекст),
    className: колонка.code === COLUMN_TOTAL ? 'font-medium' : undefined,
    reason: причина(
      колонка,
      контекст,
      вМесяце === null ? своё : значение === 0 ? вМесяце : `${своё} ${вМесяце}`,
    ),
    source: источник(колонка, строка, наЧеловеке),
  };
}

/**
 * Почему по человеку нет расчёта — настоящей причиной, если сервер её назвал.
 *
 * «Расчёт ещё не проводился» верно только тогда, когда пересчёт правда что-то даст. Если нет
 * тарифа, пересчёт не даст ничего, и совет «пересчитайте» отправлял человека жать кнопку, пока
 * он не позвонит.
 */
function почемуНеПосчитан(строка: SheetRow, ведомостьСчиталась: boolean): string {
  const известная = причинаПустоты(строка.employee, строка.row);
  if (известная !== null) {
    return ТЕКСТ_ПРИЧИНЫ[известная];
  }
  return ведомостьСчиталась
    ? 'По этому человеку расчёт ещё не проводился: сумма неизвестна, а не равна нулю.'
    : 'Расчёт по этой ведомости ещё не проводился: суммы неизвестны, а не равны нулю.';
}

/**
 * Откуда взять число ячейки.
 *
 * Порядок один и тот же для всех колонок: сначала известные поля, потом суммы по кодам —
 * сперва на строке, затем на человеке. `null` означает «сервер про эту колонку ничего не
 * прислал», и это НЕ ноль.
 */
function суммаЯчейки(колонка: PayrollColumnOut, строка: SheetRow): number | null {
  const человек = строка.employee;
  switch (колонка.code) {
    case COLUMN_ACCRUED:
      return строка.row?.accrued ?? null;
    case COLUMN_ADVANCE:
      // Не только аванс месяца: неудержанный аванс прошлых месяцев сервер кладёт в ту же колонку.
      // До 13.10.2026 здесь стоял один аванс месяца, и у 82 человек сентября колонка показывала
      // ноль при удержанных 949 427 ₽.
      return авансВКолонке(человек);
    case COLUMN_OFFICIAL_PAID:
      return человек.official_paid;
    case COLUMN_OFFICIAL_REST:
      return человек.official_rest;
    case COLUMN_TOTAL:
      return человек.total_payout;
    default:
      break;
  }
  // Строго по уровню: значение берётся оттуда, где оно ЛЕЖИТ, и не подбирается «где найдётся».
  // Подбор был бы тем же удвоением, только спрятанным на шаг глубже.
  if (уровень(колонка, человек) === 'row') {
    return строка.row?.amounts?.[колонка.code] ?? null;
  }
  return человек.amounts?.[колонка.code] ?? null;
}

/** Перевод поведения сервера в поведение сетки. Единственное место, где он делается. */
function поведение(колонка: PayrollColumnOut, контекст: КонтекстКолонки): CellState['behaviour'] {
  if (колонка.behaviour === 'locked') {
    return 'blocked';
  }
  if (колонка.behaviour === 'foreign') {
    return 'linked';
  }
  if (колонка.behaviour === 'input') {
    // Ввод в ведомость подключается вместе с операциями расчёта. Пока их нет, ячейка
    // ЗАБЛОКИРОВАНА С ПРИЧИНОЙ, а не притворяется вводимой: нажатие, которое ничего не
    // сохраняет, неотличимо от сломанной программы.
    return контекст.canEdit ? 'input' : 'blocked';
  }
  return 'computed';
}

function причина(колонка: PayrollColumnOut, контекст: КонтекстКолонки, своя: string): string {
  const части: string[] = [своя];
  if (колонка.behaviour === 'locked' && колонка.lock_reason !== null) {
    части.push(колонка.lock_reason);
  }
  if (колонка.behaviour === 'input' && !контекст.canEdit) {
    части.push(контекст.blockedReason);
  }
  if (колонка.source_note !== null && колонка.source_note !== '') {
    части.push(колонка.source_note);
  }
  return части.join(' ');
}

/** Что открыть по нажатию на ячейку, которую здесь править нельзя (правило 6). */
function источник(колонка: PayrollColumnOut, строка: SheetRow, наЧеловеке: boolean) {
  if (наЧеловеке) {
    return { panel: PANEL_SHEET_PERSON, id: строка.employee.id, focus: колонка.code };
  }
  return строка.row === null
    ? undefined
    : { panel: PANEL_SHEET_ROW, id: строка.row.id, focus: колонка.code };
}

/** Пояснение к обычному, ненулевому числу: из чего оно сложилось. */
function поясненияПоКоду(колонка: PayrollColumnOut, строка: SheetRow): string {
  const человек = строка.employee;
  switch (колонка.code) {
    case COLUMN_ACCRUED:
      return 'Начислено по этой тарифной строке: сумма за период умножить на факт часов и разделить на норму.';
    case COLUMN_OFFICIAL_PAID:
      // Заголовок в бланке заказчика стоит обратно смыслу слова, и цифра рядом это доказывает.
      // Пусто бывает у роли, которой официальная часть скрыта: экран для неё и так закрыт,
      // но ноль вместо пустоты читался бы как «официальной части нет».
      return `Уже выплачено официально в связанном периоде: ${formatKopecks(человек.official_paid ?? 0)}. Заголовок бланка про «получать» — слова заказчика, значение здесь про выплаченное.`;
    case COLUMN_OFFICIAL_REST:
      return `Остаток официальной части к выплате: ${formatKopecks(человек.official_rest ?? 0)}. Вместе с уже выплаченным это ${formatKopecks((человек.official_paid ?? 0) + (человек.official_rest ?? 0))} — столько вычитается из начисленного ДО каскада удержаний.`;
    case COLUMN_ADVANCE: {
      const перенесено = перенесённыйАванс(человек);
      if (перенесено === 0) {
        return 'Принятые заявки подотчёта с указанным получателем. Удерживаются первыми, до прочих удержаний.';
      }
      const прошлых = `неудержанный аванс прошлых месяцев ${formatKopecks(перенесено)}: он удерживается вместе с долгами, после официальной части`;
      return человек.advance_total === 0
        ? `Это ${прошлых}.`
        : `Аванс этого месяца ${formatKopecks(человек.advance_total)} и ${прошлых} и аванса месяца.`;
    }
    case COLUMN_TOTAL:
      return `Всего к выплате после каскада: начислено ${formatKopecks(человек.accruals_total)}, официальная часть ${formatKopecks((человек.official_paid ?? 0) + (человек.official_rest ?? 0))}, аванс ${formatKopecks(авансВКолонке(человек))}.`;
    default:
      return колонка.kind === 'deduction'
        ? 'Удержание по человеку, а не по тарифной строке: оно применяется ровно один раз, сколько бы постов у него ни было.'
        : 'Значение по колонке шаблона.';
  }
}

/**
 * Подвал колонки.
 *
 * Начислено и всего к выплате берутся ИЗ ИТОГОВ СЕРВЕРА: деньги считает он, а не браузер.
 * Остальные денежные колонки складываются здесь целыми копейками — это точно и без округлений,
 * но источником истины остаётся сервер, и расхождение по итогу видно в полосе итогов, а не
 * прячется выбором одного из двух чисел.
 */
function подвал(
  колонка: PayrollColumnOut,
  строки: readonly SheetRow[],
  контекст: КонтекстКолонки,
): CellState | null {
  if (колонка.code === COLUMN_HOURS || колонка.code === COLUMN_RATE) {
    // Ставки не складываются: сумма ставок это не величина. Часы объекта живут в табеле и в
    // его полосе итогов, дублировать их сюда незачем.
    return null;
  }
  if (колонка.code === COLUMN_ACCRUED) {
    return { text: итогомВПодвале(контекст.totals.accrued, контекст), behaviour: 'computed' };
  }
  if (колонка.code === COLUMN_TOTAL) {
    return {
      text: итогомВПодвале(контекст.totals.total_payout, контекст),
      behaviour: 'computed',
      className: 'font-medium',
    };
  }
  if (колонка.kind === 'service') {
    return null;
  }
  const итог = sumShown(строки, (строка) => {
    // Тот же уровень, что и в ячейке: иначе подвал сложил бы не то, что человек видит глазами.
    if (уровень(колонка, строка.employee) === 'employee' && !строка.isFirstOfEmployee) {
      return 0;
    }
    return суммаЯчейки(колонка, строка);
  });
  return { text: итогомВПодвале(итог, контекст), behaviour: 'computed' };
}

/**
 * Итог столбца в подвале, согласованный с ячейками над ним.
 *
 * Нулевой итог по нерассчитанной ведомости — это «не знаем», ровно как и в ячейках. Написав в
 * подвале «0,00» там, где в столбце стоят прочерки, мы получаем экран, который сам себе
 * противоречит: сверху величина неизвестна, снизу она равна нулю. По такому подвалу человек
 * решает, что к выплате ничего не положено.
 */
function итогомВПодвале(значение: number, контекст: КонтекстКолонки): string {
  return значение === 0 && !контекст.calculated ? '—' : formatKopecks(значение);
}

/** Копейки в то, что человек будет править: «25934.00» правится хуже, чем «25 934». */
function рублямиДляПравки(копейки: number): string {
  if (копейки === 0) {
    return '';
  }
  const рубли = Math.trunc(Math.abs(копейки) / 100);
  const остаток = Math.abs(копейки) % 100;
  const знак = копейки < 0 ? '-' : '';
  return остаток === 0 ? `${знак}${рубли}` : `${знак}${рубли},${String(остаток).padStart(2, '0')}`;
}

/**
 * Проверка введённого. Одна на ввод с клавиатуры и на вставку из буфера: иначе вставленное
 * проверялось бы иначе, чем набранное, и в деньги попадал бы мусор.
 *
 * Пустое значение законно и означает «убрать сумму»: сервер примет ноль. Отрицательных сумм в
 * ведомости не бывает вовсе — взыскание проводится понижением тарифа, а не минусом в ячейке.
 */
export function validateCell(значение: string, _cell: unknown, columnId: string): string | null {
  if (значение === '') {
    return null;
  }
  const разбор = parseRublesToKopecks(значение);
  if (!разбор.ok) {
    return moneyErrorText(разбор.reason, `сумму по колонке «${columnId}»`);
  }
  return null;
}
